import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../app/store";
import {
  selectHttpDevicesInRoom,
  selectHttpGetMultiplacerDeviceTypesData,
  selectNonPublishedDevicesAsPixPolygons,
} from "./devices/deviceSlice";
import { IPixDimensionLine, IPixPolygon, IPixPath, IPixCircle, PixBimViewerAction } from "@multiconsult-group/multiplacer-ui";
import {
  convertSingleDeviceToPixPolygon,
  getViewerPathAirTerminalDirectionsFromDevices,
  viewerDeviceHasValidPolygons,
} from "./devices/deviceUtils";
import { filterAndTransformMcBiItemsToPixPolygons } from "./transformMCBimItemsToPolygons";
import {
  selectActivePartialMcbimSpace,
  selectMcBimSpaceFromSelectedRoomNr,
} from "../McBim/mcbimSelectors";
import { selectHttpDevices } from "./devices/deviceSelectors";
import {
  buildDimensionLinesFromPointToPolygonSidesWithPreferredAxis,
  calculateBestPolygonOverlap,
  DeviceCategoryEnum,
  IDevice,
  IMcBimItem,
} from "@multiconsult-group/multiplacer-core";
import { selectCompCodePath } from "../globalSelectors";
import { selectMmiPath } from "../globalSelectors";
import { selectMcBimItemsForViewer } from "../McBim/mcbimItemSelectors";
import { selectHttpMcBimItemsInActiveStandardRoom } from "../McBim/McBimSlice";
import { selectRoomColorings } from "../Room/roomSelectors";
import { selectActiveSingleRoomSummary } from "../Room/roomMcbimSharedSelectors";
import {
  createTileValidationContext,
  getValidationsForSpace,
  isTileInCeilingGridValidWithContext,
} from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import { parseAllCeilingGrids } from "@multiconsult-group/multiplacer-core";
import { MCBimSpaceFillColors, PartialMcBimSpace } from "../utils";
import { selectedSpaceNumber } from "./designSelectors";
import { selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom } from "./devices/SelectedRooms/selectedRoomDevicesSelectors";
import { getPolygonColorsOfPixPolygonSpace } from "../RoomColoring";
import {
  selectMcbimItemHitSetByFreeTextSearch,
  selectMcbimSpaceHitSetByFreeTextSearch,
  selectSpaceNumbersWithMcbimItemHitsByFreeTextSearch,
} from "../ViewerRedux/viewerSelectors";
import { selectCeilingGridPixPaths } from "./ceilingSelectors";
import { selectClickedMcBimItemId } from "../ViewerRedux/viewerSlice";
import { IS_DEV } from "../config";
import { selectRoutingPixPaths } from "./routing/routingSelectors";
import {
  selectHttpCeilingFreeTilesByFloorResult,
  selectHttpGetRoomsCurrentFloorResult,
} from "../Room/httpRoomSelectors";
import {
  selectHttpMcBimCeilingItems,
  selectHttpMcbimSpacesResult,
} from "../McBim/httpMcBimSelectors";
import {
  selectAutoPipeRoutingIsRunning,
  selectShouldShowAutoPipeRoutingRunProgress,
} from "./pipeRoutingSlice";
import { selectDevicesForSelectedAutoPipeRoutingSystemType } from "./routing/autoPipeRouting/autoPipeRoutingSelectors";
import {
  selectRoomNumberingNumberingOrigin,
  selectRoomNumberingRestrictionLines,
  selectRoomNumberingSelectedRestrictionLineId,
  selectRoomNumberingRestrictionLinePlacementMode,
} from "../RoomNumbering/roomNumberingSlice";
import type { IRoomNumberingHeatmapEdge, IRoomNumberingHeatmapNode } from "@multiconsult-group/multiplacer-core";
import {
  selectVisibleRoomNumberingHeatmapEdges,
  selectVisibleRoomNumberingHeatmapNodes,
} from "../RoomNumbering/roomNumberingSelectors";
import { selectHasPendingAutoPipeRoutingPublish } from "./routing/unsavedRoutingSlice";
import {
  selectRoomSectionCenterHandlePolygon,
  selectRoomSectionDirectionPixPolygons,
} from "../RoomSectionViews/roomSectionViewSelectors";
import { filterOutContainerMcBimSpaces } from "./containerSpaceFilter";
import { selectHttpMcBimItemsInSelectedRoom } from "../RoomSideBar/singleRoomSelectors";
import { selectModRotation } from "./designSelector";
import {
  selectCalculatedSelectedRoomCeilingFreeTiles,
  selectShowFreeTilesInSelectedRoom,
} from "../RoomSideBar/MultiplacerCeilingDevices/ceilingSlice";
import { selectDrofusChangesRoomColorByRoomNumber } from "../DrofusChanges/drofusChangesSelectors";

const selectedDeviceId = (s: IRootState) => s.device.selectedDeviceId;
const selectHideDevices = (s: IRootState) => s.design.hideDevices;
const selectTrueSize = (s: IRootState) => s.design.showTrueSizeOfDevices;
const selectDragSelectSpaces = (s: IRootState) => s.design.dragSelectSpaces;
const selectMeasurementToolActive = (s: IRootState) => s.design.measurementToolActive;
const selectedRooms = (state: IRootState) => state.design.selectedRooms;
const copySpaceModeActive = (state: IRootState) => state.design.copySpaceModeActive;
const spaceValidationRun = (state: IRootState) => state.design.spaceValidationRun;
const activeSpaceValidationSpecs = (state: IRootState) => state.design.activeSpaceValidationSpecs;
const activeRoomColoringId = (state: IRootState) => state.design.activeRoomColoringId;
const selectDeviceIdOnTheMove = (state: IRootState) => state.design.deviceOnTheMove;
const selectNonPublischedDevicesFromAlgos = (state: IRootState) =>
  state.design.nonPublischedDevicesFromAlgos;
const selectRunSpaceValidationsInSelectedRooms = (state: IRootState) =>
  state.design.runSpaceValidationsInSelectedRooms;
const selectCurrentActiveModule = (state: IRootState) => state.design.activeModule;
const selectShowContainerSpaces = (state: IRootState) => state.design.showContainerSpaces;
const SPACE_STROKE_WIDTH = "4";
const HIGHLIGHTED_SPACE_STROKE_WIDTH = "8";
const ACTIVE_SPACE_STROKE_WIDTH = "6";

const getPolygonMatchPercentage = (
  sourcePolygon?: number[][],
  targetPolygon?: number[][]
): number | null => {
  const overlap = calculateBestPolygonOverlap(sourcePolygon, targetPolygon);
  if (overlap == null) {
    return null;
  }

  return Math.round(overlap * 100);
};

const selectActiveDeviceInRoom = createSelector(
  [selectHttpDevicesInRoom, selectedDeviceId],
  (devicesInRoom, activeDeviceId): IDevice | undefined => {
    if (activeDeviceId == null) {
      return undefined;
    }

    return devicesInRoom.find((device) => device._id === activeDeviceId);
  }
);

export const selectMcBimSpacesPixPolygons = createSelector(
  [
    selectedSpaceNumber, // 1
    selectDragSelectSpaces, // 2
    selectedRooms, // 3
    copySpaceModeActive, // 4
    spaceValidationRun, // 5
    activeSpaceValidationSpecs, // 6
    activeRoomColoringId, // 7
    selectDeviceIdOnTheMove, // 10
    selectNonPublischedDevicesFromAlgos, // 11
    selectHttpMcbimSpacesResult, // 12
    selectActiveSingleRoomSummary, // 13
    selectHttpDevicesInRoom, // 14
    selectHttpMcBimItemsInActiveStandardRoom, // 15
    selectHttpMcBimItemsInSelectedRoom,
    selectHttpGetRoomsCurrentFloorResult, // 16
    selectCompCodePath, // 17
    selectMcBimSpaceFromSelectedRoomNr, // 18
    selectRoomColorings,
    selectMcbimSpaceHitSetByFreeTextSearch,
    selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom,
    selectRunSpaceValidationsInSelectedRooms,
    selectSpaceNumbersWithMcbimItemHitsByFreeTextSearch,
    selectShouldShowAutoPipeRoutingRunProgress,
    selectCurrentActiveModule,
    selectShowContainerSpaces,
    selectDrofusChangesRoomColorByRoomNumber,
  ],
  (
    mcbimSpaceNumber, // 1
    dragSelectSpaces, // 2
    selectedRooms, // 3
    copySpaceModeActive, // 4
    spaceValidationRun, // 5
    activeSpaceValidationSpecs, // 6
    activeRoomColoringId, // 7
    deviceIdOnTheMove, // 10
    nonPublischedDevicesFromAlgos, // 11
    mcbimSpaces, // 12
    singleRoom, // 13
    devicesInRoom: IDevice[], // 14
    mcbimItemsInStandardSpace: IMcBimItem[], // 15
    mcbimItemsInSpace,
    rooms, // 16
    compCodePath, // 17
    singleSpace, // 18
    roomColorings,
    mcbimSpaceHitSetByFreeTextSearch,
    spaceValidationsMatrixForSelectedRoomsExcludingStandardRoom,
    runSpaceValidationsInSelectedRooms,
    spacesWithMcbimItemsHitsByFreeTextSearch,
    autoPipeRoutingIterationIsRunning,
    activeModule,
    showContainerSpaces,
    drofusChangesRoomColorByRoomNumber
  ) => {
    const _mcbimSpaces = showContainerSpaces
      ? (mcbimSpaces ?? [])
      : filterOutContainerMcBimSpaces(mcbimSpaces ?? []);
    const activeMcBimSpace = _mcbimSpaces.find((space) => space.name === mcbimSpaceNumber);

    const uniqueSpaceNumbersInNonPublishedDevices = new Set(
      nonPublischedDevicesFromAlgos.map((device) => device.roomNumber)
    );

    const deviceOnTheMove = devicesInRoom.find((device) => device._id === deviceIdOnTheMove);

    return _mcbimSpaces.reduce((acc, mcbimSpace) => {
      if (mcbimSpace.longName?.toUpperCase() === "AREA" || mcbimSpace.polygon == null) {
        return acc;
      }

      const room = rooms?.[mcbimSpace.name];
      let pixPolygon = convertMcBimSpaceToPixPolygon({
        mcBimSpace: mcbimSpace,
        dragSelectSpaces,
        roomNumberingValue:
          activeModule === "ROOM_NUMBERING"
            ? (room?.roomNumbering?.value ?? room?.roomNumbering?.suggested)
            : undefined,
      });

      if (pixPolygon != null) {
        if (room?.multiplacerCeilingDevicesLocked?.bool === true) {
          pixPolygon.text.color = "red";
        }

        const copySpaceModePolygonMatchPct =
          copySpaceModeActive &&
          activeMcBimSpace?.longName != null &&
          activeMcBimSpace.longName === mcbimSpace.longName
            ? getPolygonMatchPercentage(activeMcBimSpace.polygon, mcbimSpace.polygon)
            : null;

        if (copySpaceModePolygonMatchPct != null) {
          pixPolygon.text.values = [
            mcbimSpace.longName ?? "-",
            mcbimSpace.name,
            `${copySpaceModePolygonMatchPct} %`,
          ];
        }

        let spaceIsValid: boolean | undefined;

        if (mcbimSpaceNumber != null && singleRoom != null && singleSpace != null) {
          if (mcbimSpaceNumber === mcbimSpace.name) {
            const spaceValidations = getValidationsForSpace({
              singleRoom: singleRoom,
              singleSpace: singleSpace,
              devicesInRoom: devicesInRoom,
              mcbimItemsInStandardSpace: mcbimItemsInStandardSpace,
              mcbimItemsInSpace,
              activeSpaceValidationSpecs,
              compCodePath: compCodePath as string,
            });

            spaceIsValid =
              spaceValidations.filter((validation) => validation.status === "WARNING").length === 0;

            // tjock kant nÃ¤r man stÃ¥r i space-valideringslÃ¤ge
          }
        }

        const isActiveRoom = activeMcBimSpace?.name === mcbimSpace.name;
        const isSelectedRoom = selectedRooms.includes(mcbimSpace.name);
        const polygonColors = dragSelectSpaces
          ? {}
          : getPolygonColorsOfPixPolygonSpace({
              theSpaceNumber: mcbimSpace.name,
              activeSpace:
                activeMcBimSpace != null
                  ? {
                      spaceNumber: activeMcBimSpace.name,
                      spaceLongName: activeMcBimSpace.longName as string,
                    }
                  : null,
              mcbimSpacesNameAndLongName: _mcbimSpaces.map((space) => ({
                spaceNumber: space.name,
                spaceLongName: space.longName as string,
              })),
              selectedSpaces: selectedRooms,
              spaceValid: spaceIsValid,
              copySpaceModeActive,
              devicesExistsInSpace: devicesInRoom.length > 0,
              spaceValidationRun,
              activeRoomColoringId,
              room,
              space: mcbimSpace,
              freeTextSearchHit: mcbimSpaceHitSetByFreeTextSearch?.has(mcbimSpace.name) ?? false,
              deviceOnTheMove,
              nonPublischedDevicesFromAlgosSpaceNumbers: Array.from(
                uniqueSpaceNumbersInNonPublishedDevices
              ),
              roomColorings,
              spaceValidationsMatrixForSelectedRoomsExcludingStandardRoom,
              runSpaceValidationsInSelectedRooms,
              spacesWithMcbimItemsHitsByFreeTextSearch,
              autoPipeRoutingIterationIsRunning,
              copySpaceModePolygonMatchPct,
            });

        if (polygonColors.fill !== undefined) {
          pixPolygon.fillColor = polygonColors.fill as string;
        }

        if (polygonColors.stroke !== undefined) {
          pixPolygon.strokeColor = polygonColors.stroke;
          pixPolygon.strokeWidth = HIGHLIGHTED_SPACE_STROKE_WIDTH;
        } else {
          pixPolygon.strokeWidth = SPACE_STROKE_WIDTH;
          pixPolygon.strokeColor = "#000000";
        }

        if (isActiveRoom) {
          pixPolygon.strokeColor = "#8cb9efff";
          pixPolygon.strokeWidth = ACTIVE_SPACE_STROKE_WIDTH;
        }

        if (isSelectedRoom && !autoPipeRoutingIterationIsRunning) {
          pixPolygon.fillColor = MCBimSpaceFillColors.LIGHT_GRAY;
        }

        const drofusChangeColor = drofusChangesRoomColorByRoomNumber[mcbimSpace.name];
        if (drofusChangeColor && !autoPipeRoutingIterationIsRunning) {
          pixPolygon.fillColor = drofusChangeColor.fill;
          pixPolygon.strokeColor = drofusChangeColor.stroke;
          pixPolygon.strokeWidth = HIGHLIGHTED_SPACE_STROKE_WIDTH;
        }
      }

      if (pixPolygon !== undefined) {
        acc.push(pixPolygon);
      }

      return acc;
    }, [] as IPixPolygon[]);
  }
);

interface IConvertMcBimSpaceToPixPolygonArgs {
  mcBimSpace: PartialMcBimSpace | undefined;
  dragSelectSpaces: boolean;
  roomNumberingValue?: string;
  showText?: boolean;
}

export const convertMcBimSpaceToPixPolygon = ({
  mcBimSpace,
  dragSelectSpaces,
  roomNumberingValue,
  showText = true,
}: IConvertMcBimSpaceToPixPolygonArgs): IPixPolygon | undefined => {
  if (mcBimSpace == null) {
    return undefined;
  }
  if (mcBimSpace.polygon == null) {
    return undefined;
  }

  return {
    id: mcBimSpace.name,
    points: mcBimSpace.polygon as number[][],
    fillColor: mcBimSpace.searchHit ? "#ff0000" : (mcBimSpace.fillColor ?? "#ffffff"),
    strokeColor: mcBimSpace.fillColor ?? "#000",
    strokeWidth: SPACE_STROKE_WIDTH,
    text: {
      values:
        roomNumberingValue != null
          ? [mcBimSpace.name, mcBimSpace.longName ?? "-", roomNumberingValue]
          : [mcBimSpace.name, mcBimSpace.longName ?? "-"],
      color: "#000000",
      fontSize: "1",
      show: showText,
    },
    isMoveable: false,
    tooltip: undefined, // `${mcBimSpace.name} -  ${mcBimSpace.longName}`,
    isDragSelectable: dragSelectSpaces,
  };
};

const selectDeviceOnTheMove = (state: IRootState) => state.design.deviceOnTheMove;
const selectActiveSpaceValidationSpecs = (state: IRootState) =>
  state.design.activeSpaceValidationSpecs;
const MAX_VALIDATION_TILE_OVERLAY_TILES = 1000;

const isValidTilePoint = (point: unknown): point is [number, number] => {
  if (!Array.isArray(point) || point.length !== 2) return false;

  const [x, y] = point;
  return typeof x === "number" && typeof y === "number" && Number.isFinite(x) && Number.isFinite(y);
};

const isValidTileBoundingBox = (boundingBox: unknown): boundingBox is [number, number][] =>
  Array.isArray(boundingBox) && boundingBox.length === 5 && boundingBox.every(isValidTilePoint);

export const selectValidationTilesPixPolygon = createSelector(
  [
    selectDeviceOnTheMove,
    selectedSpaceNumber,
    selectActiveSpaceValidationSpecs,
    selectHttpGetRoomsCurrentFloorResult,
    selectHttpMcbimSpacesResult,
    selectHttpDevicesInRoom,
    selectHttpMcBimItemsInSelectedRoom,
    selectCompCodePath,
  ],
  (
    deviceIdOnTheMove,
    spaceNumber,
    spaceValidationSpecs,
    rooms,
    spaces,
    devicesInRoom,
    mcbimItemsInSpace,
    compCodePath
    // spacesAndRoomsAndDevicesAndProjectNo
  ): IPixPolygon[] => {
    try {
      if (spaceNumber == null || deviceIdOnTheMove === undefined) {
        return [];
      }
      const room = rooms[spaceNumber];
      if (!room) return [];
      const mcbimSpace = spaces.find((space) => space.name === spaceNumber);
      if (!mcbimSpace) return [];

      let ceilingGrids = mcbimSpace?.ceilingGrids;
      if (ceilingGrids === undefined) ceilingGrids = [];

      const tilesArray = parseAllCeilingGrids(ceilingGrids);

      const allTiles = tilesArray?.flat(2) ?? [];
      if (allTiles.length > MAX_VALIDATION_TILE_OVERLAY_TILES) {
        return [];
      }

      const validationContext = createTileValidationContext({
        devicesInRoom,
        deviceOnTheMoveId: deviceIdOnTheMove,
        room,
        space: mcbimSpace,
        mcbimItemsInStandardSpace: [],
        mcbimItemsInSpace,
        activeSpaceValidationSpecs: spaceValidationSpecs,
        compCodePath: compCodePath as string,
      });
      if (!validationContext) {
        return [];
      }

      const validColor = "#8dca8d";
      const invalidColor = "#dc4242";

      const pixPolygons: IPixPolygon[] = [];
      let anyTileValid = false;

      allTiles.forEach((tile, index) => {
        if (!isValidTileBoundingBox(tile.boundingBox)) {
          return;
        }

        const isTileValid = isTileInCeilingGridValidWithContext(validationContext, tile);
        anyTileValid ||= isTileValid;
        pixPolygons.push({
          id: `validation-tile-${index}`,
          points: tile.boundingBox,
          fillColor: isTileValid ? validColor : invalidColor,
          strokeColor: "#dc4242",
          strokeWidth: "1",
          text: {
            show: false,
            values: [],
            color: "#000",
            fontSize: "12px",
          },
          tooltip: "",
          isMoveable: false,
          isDragSelectable: false,
        });
      });

      if (anyTileValid) {
        return pixPolygons;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }
);

const isNotNull = <T>(v: T | null | undefined): v is T => v != null;

export const selectMultiplacerDevicesPixPolygons = createSelector(
  [
    selectHttpDevices,
    selectHttpGetRoomsCurrentFloorResult,
    selectedDeviceId,
    selectHttpGetMultiplacerDeviceTypesData,
    selectHideDevices,
    selectTrueSize,
    selectDragSelectSpaces,
  ],
  (
    devicesResp,
    roomsResp,
    selectedDeviceId,
    typeDevicesResp,
    hideDevices,
    showTrueSize,
    dragSelectSpaces
  ): IPixPolygon[] => {
    if (dragSelectSpaces) {
      return [];
    }

    if (devicesResp == null || devicesResp.length === 0 || hideDevices) {
      return [];
    }
    const devices = devicesResp
      .map((deviceItem) =>
        convertSingleDeviceToPixPolygon({
          deviceItem,
          typeDevices: typeDevicesResp,
          selectedDeviceId,
          showTrueSize,
          room: roomsResp?.[deviceItem.roomNumber],
          // Only the selected device is moveable
          isMoveable: selectedDeviceId != null && deviceItem._id === selectedDeviceId,
        })
      )
      .filter(isNotNull)
      .filter((pixPolygon) => viewerDeviceHasValidPolygons(pixPolygon));

    return devices;
  }
);

export const selectAutoPipeRoutingDevicesPixPolygons = createSelector(
  [
    selectDevicesForSelectedAutoPipeRoutingSystemType,
    selectHttpGetRoomsCurrentFloorResult,
    selectedDeviceId,
    selectHttpGetMultiplacerDeviceTypesData,
    selectHideDevices,
    selectTrueSize,
    selectDragSelectSpaces,
  ],
  (
    devicesResp,
    roomsResp,
    selectedDeviceId,
    typeDevicesResp,
    hideDevices,
    showTrueSize,
    dragSelectSpaces
  ): IPixPolygon[] => {
    if (dragSelectSpaces) {
      return [];
    }

    if (devicesResp == null || devicesResp.length === 0 || hideDevices) {
      return [];
    }

    return devicesResp
      .map((deviceItem) =>
        convertSingleDeviceToPixPolygon({
          deviceItem,
          typeDevices: typeDevicesResp,
          selectedDeviceId,
          showTrueSize,
          room: roomsResp?.[deviceItem.roomNumber],
          isMoveable: selectedDeviceId != null && deviceItem._id === selectedDeviceId,
        })
      )
      .filter(isNotNull)
      .filter((pixPolygon) => viewerDeviceHasValidPolygons(pixPolygon));
  }
);

const selectDeviceMode = (s: IRootState) => s.design.deviceViewMode;
export const selectPixPolygonsToShow = createSelector(
  [selectMultiplacerDevicesPixPolygons, selectNonPublishedDevicesAsPixPolygons, selectDeviceMode],
  (multiPlacerDevices, nonPublishedDevices, deviceMode) => {
    if (deviceMode === "devices") {
      return [...multiPlacerDevices, ...nonPublishedDevices];
    }
  }
);
const selectMcbimItemColorMode = (s: IRootState) => s.design.mcbimItemColorMode;
export const selectPixPolygonMcBimItems = createSelector(
  [
    selectMcBimItemsForViewer,
    selectMcbimItemColorMode,
    selectMmiPath,
    selectMcbimItemHitSetByFreeTextSearch,
    selectClickedMcBimItemId,
  ],
  (mcBimItems, mcbimItemColorMode, mmiPath, mcbimItemHitSet, clickedMcBimItemId) => {
    if (mcBimItems == null) {
      return [];
    }
    return filterAndTransformMcBiItemsToPixPolygons(
      mcBimItems,
      mcbimItemColorMode,
      mmiPath,
      mcbimItemHitSet,
      clickedMcBimItemId
    );
  }
);

export const selectUniqueMmiValuesInMcBimItems = createSelector(
  [selectMcBimItemsForViewer],
  (mcBimItems): number[] => {
    if (mcBimItems == null || mcBimItems.length === 0) {
      return [];
    }

    const set = new Set<number>();

    for (const item of mcBimItems) {
      const raw = item.mmi;
      if (raw == null) continue;

      // item.mmi might be "3", 3, " 3 ", etc.
      const n = typeof raw === "number" ? raw : Number(String(raw).trim());
      if (!Number.isFinite(n)) continue;

      set.add(n);
    }

    return Array.from(set).sort((a, b) => a - b);
  }
);

export type IMmiLegendStat = {
  mmi: number;
  total: number;
  byClass: Record<string, number>; // key: instanceClass
};

export const selectPixPolygonMcBimCeilingItems = createSelector(
  [selectHttpMcBimCeilingItems, selectMcbimItemColorMode, selectMmiPath],
  (mcBimItems, mcbimItemColorMode, mmiPath) => {
    if (mcBimItems == null) {
      return [];
    }
    return filterAndTransformMcBiItemsToPixPolygons(mcBimItems, mcbimItemColorMode, mmiPath);
  }
);

export const selectPixDevicesAndMcBimSpaces = createSelector(
  [selectMultiplacerDevicesPixPolygons, selectMcBimSpacesPixPolygons],
  (devices, spaces) => {
    return [...spaces, ...devices];
  }
);

const selectActiveModule = (state: IRootState) => state.design.activeModule;
export const selectMlHeatmapTiles = (state: IRootState) => state.design.mlHeatmapTiles;
const CEILING_MCBIM_ITEM_OPACITY = 0.35;
const VIEWER_POLYGON_Z_INDEX = {
  SPACE: 0,
  IFC_ITEM: 10,
  VALIDATION: 15,
  DEVICE: 20,
  FREE_TILE: 30,
  OVERLAY: 40,
  HEATMAP: 50,
} as const;

const withOpacity = (polygons: IPixPolygon[], opacity: number): IPixPolygon[] =>
  polygons.map((polygon) => ({
    ...polygon,
    opacity,
  }));

const withZIndex = (polygons: IPixPolygon[], zIndex: number): IPixPolygon[] =>
  polygons.map((polygon) => ({
    ...polygon,
    zIndex,
  }));

const heatmapTileToPixPolygon = (
  tile: { points: number[][]; color: [number, number, number, number] },
  index: number
): IPixPolygon => {
  const [r, g, b, a] = tile.color;
  const toHex = (value: number) => value.toString(16).padStart(2, "0");

  return {
    id: `ml-heatmap-tile-${index}`,
    points: tile.points,
    fillColor: `#${toHex(r)}${toHex(g)}${toHex(b)}`,
    strokeColor: undefined,
    strokeWidth: undefined,
    text: {
      show: false,
      values: [],
      color: undefined,
      fontSize: undefined,
    },
    tooltip: undefined,
    isNotClickable: true,
    isMoveable: false,
    isDragSelectable: false,
    opacity: a / 255,
    zIndex: VIEWER_POLYGON_Z_INDEX.HEATMAP,
  };
};

export const selectFreeTilePixPolygons = createSelector(
  [selectShowFreeTilesInSelectedRoom, selectCalculatedSelectedRoomCeilingFreeTiles],
  (showFreeTiles, freeTiles): IPixPolygon[] => {
    if (!showFreeTiles || !freeTiles?.length) return [];

    return freeTiles.map((tile): IPixPolygon => ({
      id: `free-ceiling-tile-${tile.gridIndex}-${tile.rowIndex}-${tile.columnIndex}`,
      points: tile.boundingBox,
      fillColor: "#69db7c",
      strokeColor: "#2f9e44",
      strokeWidth: "1",
      text: {
        show: false,
        values: [],
        color: undefined,
        fontSize: undefined,
      },
      tooltip: "Ledig",
      isMoveable: false,
      isDragSelectable: false,
      isNotClickable: true,
      opacity: 0.45,
      zIndex: VIEWER_POLYGON_Z_INDEX.FREE_TILE,
    }));
  }
);

export const selectFloorFreeTilePixPolygons = createSelector(
  [selectHttpCeilingFreeTilesByFloorResult],
  (result): IPixPolygon[] => {
    if (!result?.rooms.length) return [];

    return result.rooms.flatMap((room) =>
      room.ceilingFreeTiles.map((tile): IPixPolygon => ({
        id: `floor-free-ceiling-tile-${room.roomNumber}-${tile.gridIndex}-${tile.rowIndex}-${tile.columnIndex}`,
        points: tile.boundingBox,
        fillColor: "#69db7c",
        strokeColor: "#2f9e44",
        strokeWidth: "1",
        text: {
          show: false,
          values: [],
          color: undefined,
          fontSize: undefined,
        },
        tooltip: "Ledig",
        isMoveable: false,
        isDragSelectable: false,
        isNotClickable: true,
        opacity: 0.45,
        zIndex: VIEWER_POLYGON_Z_INDEX.FREE_TILE,
      }))
    );
  }
);

export const selectFinalPixPolygons = createSelector(
  [
    selectMcBimSpacesPixPolygons,
    selectMultiplacerDevicesPixPolygons,
    selectAutoPipeRoutingDevicesPixPolygons,
    selectPixPolygonMcBimCeilingItems,
    selectValidationTilesPixPolygon,
    selectDeviceMode,
    selectDeviceOnTheMove,
    selectNonPublishedDevicesAsPixPolygons,
    selectPixPolygonMcBimItems,
    selectDragSelectSpaces,
    selectActiveModule,
    selectAutoPipeRoutingIsRunning,
    selectHasPendingAutoPipeRoutingPublish,
    selectRoomSectionDirectionPixPolygons,
    selectRoomSectionCenterHandlePolygon,
    selectFreeTilePixPolygons,
    selectFloorFreeTilePixPolygons,
  ],
  (
    spaces,
    devices,
    autoPipeRoutingDevices,
    ceilingMcBimItems,
    spacePixValidations,
    deviceMode,
    deviceOnTheMove,
    nonPublishedDevices,
    pixPolygonArkMcBimItems,
    dragSelectSpaces,
    activeModule,
    autoPipeRoutingIsRunning,
    hasPendingAutoPipeRoutingPublish,
    roomSectionDirectionPixPolygons,
    roomSectionCenterHandlePolygon,
    freeTilePixPolygons,
    floorFreeTilePixPolygons
  ): IPixPolygon[] => {
    if (autoPipeRoutingIsRunning || hasPendingAutoPipeRoutingPublish) {
      return [
        ...withZIndex(spaces, VIEWER_POLYGON_Z_INDEX.SPACE),
        ...withZIndex(autoPipeRoutingDevices, VIEWER_POLYGON_Z_INDEX.DEVICE),
      ];
    }

    const roomSectionDirectionPolygons =
      roomSectionDirectionPixPolygons == null
        ? []
        : Object.values(roomSectionDirectionPixPolygons).flat();
    const roomSectionOverlayPolygons = [
      ...roomSectionDirectionPolygons,
      ...(roomSectionCenterHandlePolygon == null ? [] : [roomSectionCenterHandlePolygon]),
    ];
    const withRoomSectionOverlays = (polygons: IPixPolygon[]): IPixPolygon[] => [
      ...polygons,
      ...withZIndex(roomSectionOverlayPolygons, VIEWER_POLYGON_Z_INDEX.OVERLAY),
    ];
    const spacePolygons = withZIndex(spaces, VIEWER_POLYGON_Z_INDEX.SPACE);
    const devicePolygons = withZIndex(devices, VIEWER_POLYGON_Z_INDEX.DEVICE);
    const nonPublishedDevicePolygons = withZIndex(
      nonPublishedDevices,
      VIEWER_POLYGON_Z_INDEX.DEVICE
    );
    const validationPolygons = withZIndex(spacePixValidations, VIEWER_POLYGON_Z_INDEX.VALIDATION);
    const freeTilePolygons = withZIndex(
      [...freeTilePixPolygons, ...floorFreeTilePixPolygons],
      VIEWER_POLYGON_Z_INDEX.FREE_TILE
    );
    const ceilingMcbimItemPolygons =
      activeModule === "CEILING"
        ? withZIndex(
            withOpacity(pixPolygonArkMcBimItems, CEILING_MCBIM_ITEM_OPACITY),
            VIEWER_POLYGON_Z_INDEX.IFC_ITEM
          )
        : withZIndex(pixPolygonArkMcBimItems, VIEWER_POLYGON_Z_INDEX.IFC_ITEM);
    const ceilingItemPolygons =
      activeModule === "CEILING"
        ? withZIndex(
            withOpacity(ceilingMcBimItems, CEILING_MCBIM_ITEM_OPACITY),
            VIEWER_POLYGON_Z_INDEX.IFC_ITEM
          )
        : withZIndex(ceilingMcBimItems, VIEWER_POLYGON_Z_INDEX.IFC_ITEM);

    if (dragSelectSpaces) {
      return withRoomSectionOverlays(spacePolygons);
    }

    if (activeModule === "RFP") {
      return withRoomSectionOverlays([...spacePolygons, ...ceilingMcbimItemPolygons]);
    }

    if (activeModule === "CEILING") {
      if (deviceMode === "devices") {
        if (deviceOnTheMove == null) {
          return withRoomSectionOverlays([
            ...spacePolygons,
            ...ceilingMcbimItemPolygons,
            ...freeTilePolygons,
            ...devicePolygons,
            ...nonPublishedDevicePolygons,
          ]);
        } else {
          return withRoomSectionOverlays([
            ...spacePolygons,
            ...ceilingMcbimItemPolygons,
            ...validationPolygons,
            ...freeTilePolygons,
            ...devicePolygons,
          ]);
        }
      }
      if (deviceMode === "ifc") {
        return withRoomSectionOverlays([
          ...spacePolygons,
          ...ceilingMcbimItemPolygons,
          ...ceilingItemPolygons,
          ...freeTilePolygons,
        ]);
      }
    }

    return withRoomSectionOverlays([...spacePolygons, ...ceilingMcbimItemPolygons]);
  }
);

const HEATMAP_OPACITY = 0.8;
const toHex = (v: number) => v.toString(16).padStart(2, "0");
const rgbToHex = ([r, g, b]: [number, number, number]) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const roomNumberingHeatmapEdgeToPixPath = (edge: IRoomNumberingHeatmapEdge): IPixPath => ({
  id: edge.id,
  points: edge.points,
  strokeColor: rgbToHex(edge.color),
  strokeWidth: edge.hasDistance ? "0.5" : "0.3",
  opacity: edge.hasDistance ? HEATMAP_OPACITY : 0.3,
});

const roomNumberingHeatmapNodeToPixCircle = (node: IRoomNumberingHeatmapNode): IPixCircle => ({
  id: node.id,
  position: node.position,
  radius: node.radiusM,
  color: rgbToHex(node.color),
  opacity: HEATMAP_OPACITY,
});

export const selectRoomNumberingHeatmapPaths = createSelector(
  [selectVisibleRoomNumberingHeatmapEdges],
  (edges): IPixPath[] => edges.map(roomNumberingHeatmapEdgeToPixPath)
);

export const selectRoomNumberingHeatmapCircles = createSelector(
  [selectVisibleRoomNumberingHeatmapNodes],
  (nodes): IPixCircle[] => nodes.map(roomNumberingHeatmapNodeToPixCircle)
);

export const NUMBERING_ORIGIN_POLYGON_ID = "numbering-origin";

const createCirclePolygonPoints = (
  [centerX, centerY]: [number, number],
  radius: number,
  pointCount = 24,
): number[][] =>
  Array.from({ length: pointCount }, (_, index) => {
    const angle = (Math.PI * 2 * index) / pointCount;
    return [
      centerX + Math.cos(angle) * radius,
      centerY + Math.sin(angle) * radius,
    ];
  });

export const selectNumberingOriginPolygons = createSelector(
  [selectActiveModule, selectRoomNumberingNumberingOrigin],
  (activeModule, numberingOrigin): IPixPolygon[] => {
    if (activeModule !== "ROOM_NUMBERING" || numberingOrigin == null) return [];
    return [{
      id: NUMBERING_ORIGIN_POLYGON_ID,
      points: createCirclePolygonPoints(numberingOrigin, 1.0),
      fillColor: "#ff6600",
      strokeColor: "#cc5200",
      strokeWidth: "0.2",
      text: {
        show: false,
        values: [],
        color: undefined,
        fontSize: undefined,
      },
      tooltip: "Startpunkt for romnummerering",
      isMoveable: true,
      isDragSelectable: false,
      opacity: 1,
      zIndex: 10,
    }];
  },
);

export const selectViewerPixPolygons = createSelector(
  [selectFinalPixPolygons, selectMlHeatmapTiles, selectNumberingOriginPolygons],
  (pixPolygons, mlHeatmapTiles, numberingOriginPolygons): IPixPolygon[] => [
    ...pixPolygons,
    ...mlHeatmapTiles.map((tile, index) => heatmapTileToPixPolygon(tile, index)),
    ...numberingOriginPolygons,
  ]
);

const RESTRICTION_LINE_PATH_PREFIX = "numbering-restriction-line-";

export const selectRoomNumberingRestrictionLinePaths = createSelector(
  [selectActiveModule, selectRoomNumberingRestrictionLines, selectRoomNumberingSelectedRestrictionLineId],
  (activeModule, restrictionLines, selectedRestrictionLineId): IPixPath[] => {
    if (activeModule !== "ROOM_NUMBERING") return [];
    return restrictionLines.map((restrictionLine) => ({
      id: `${RESTRICTION_LINE_PATH_PREFIX}${restrictionLine.id}`,
      points: [restrictionLine.start, restrictionLine.end],
      strokeColor: restrictionLine.id === selectedRestrictionLineId ? "#2266cc" : "#111111",
      strokeWidth: restrictionLine.id === selectedRestrictionLineId ? "50" : "40",
      opacity: 1,
    }));
  }
);

export const selectPixActionState = createSelector(
  [selectMeasurementToolActive, selectDragSelectSpaces, selectRoomNumberingRestrictionLinePlacementMode],
  (measurementToolActive, dragSelectSpaces, restrictionLinePlacementMode): PixBimViewerAction => {
    if (measurementToolActive) return "measure";
    if (dragSelectSpaces) return "dragSelect";
    if (restrictionLinePlacementMode) return "drawPath";
    return "normal";
  }
);

export const selectViewerPathsDeviceDirections = createSelector(
  [selectHttpDevices, selectHideDevices],
  (devices, hideDevices) =>
    getViewerPathAirTerminalDirectionsFromDevices(devices ?? [], hideDevices)
);

export const selectPixPaths = createSelector(
  [
    selectCeilingGridPixPaths,
    selectViewerPathsDeviceDirections,
    selectRoutingPixPaths,
    selectAutoPipeRoutingIsRunning,
    selectHasPendingAutoPipeRoutingPublish,
  ],
  (
    ceilingGridPaths,
    devicePaths,
    routingPaths,
    autoPipeRoutingIsRunning,
    hasPendingAutoPipeRoutingPublish
  ) => {
    const shouldHideCeilingGrid = autoPipeRoutingIsRunning || hasPendingAutoPipeRoutingPublish;
    const visibleCeilingGridPaths = shouldHideCeilingGrid ? [] : ceilingGridPaths;

    return [...visibleCeilingGridPaths, ...devicePaths, ...routingPaths];
  }
);

export const selectFloorExtent = createSelector([selectMcBimSpacesPixPolygons], (spaces) => {
  if (!spaces.length) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const poly of spaces) {
    for (const pt of poly.points ?? []) {
      const x = pt[0];
      const y = pt[1];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return null;
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
});

export const selectFloorExtentKey = createSelector([selectFloorExtent], (ext) => {
  if (!ext) return null;

  const r = (n: number) => Math.round(n * 100) / 100; // 2 desimaler, juster ved behov
  return `${r(ext.minX)}:${r(ext.minY)}:${r(ext.maxX)}:${r(ext.maxY)}`;
});

// The pivot point used by DeckBimViewer's transform: center of the flipped (y,x swapped) bounding box.
// Used to invert the 2D rotation when converting centerWorld back to IFC space for 3D sync.
export const selectTransformPivot = createSelector([selectFloorExtent], (ext) => {
  if (!ext) return null;
  // After flip, IFC (x,y) → (y,x), so flipped bbox: minX'=minY, maxX'=maxY, minY'=minX, maxY'=maxX
  return {
    x: (ext.minY + ext.maxY) / 2,
    y: (ext.minX + ext.maxX) / 2,
  };
});

export const selectActiveDeviceToActiveRoomDimensionLines = createSelector(
  [
    selectActiveDeviceInRoom,
    selectActivePartialMcbimSpace,
    selectModRotation,
    selectTransformPivot,
  ],
  (activeDevice, activeSpace, rotation, transformPivot): IPixDimensionLine[] => {
    if (activeDevice?.deviceCategory !== DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE) {
      return [];
    }

    const coordinate = activeDevice?.coordinate;
    const polygon = activeSpace?.polygon;

    if (coordinate == null || polygon == null || coordinate.length < 2 || polygon.length < 3) {
      return [];
    }

    const point: [number, number] = [coordinate[0], coordinate[1]];

    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) {
      return [];
    }

    return buildDimensionLinesFromPointToPolygonSidesWithPreferredAxis({
      polygon: polygon as [number, number][],
      point,
      ceilingGrids: activeSpace?.ceilingGrids,
      fallbackViewerTransform: transformPivot
        ? {
            pivot: [transformPivot.x, transformPivot.y],
            angleDeg: rotation,
            flip2dCoordinate: true,
          }
        : null,
    }).map((item) => item.dimensionLine);
  }
);
